import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import { Link } from 'react-router-dom';

import { getStripe } from '@/lib/stripe';
import { Icon } from '@/lib/Icon';
import { track } from '@/lib/analytics';
import { apiEndpoint } from '@/lib/api/client';

/**
 * Public no-login payment-update page for dunning links
 * (`/update-payment?c=<slug>` — short opaque code; legacy `?t=<JWT>`
 * also accepted). The credential — minted by the backend and
 * delivered in "your renewal payment failed" emails/SMS — scopes the whole
 * flow to ONE customer + past-due invoice, so no Supabase session is
 * needed: same UX class as Stripe's hosted dunning pages, on our domain.
 *
 * Flow: GET /billing/dunning-session (validates token + live resolved-check)
 * → POST setup-intent → Stripe Elements collects the new card →
 * POST complete (backend makes it the default everywhere and pays the
 * past-due invoice immediately). A decline comes back inline and the
 * patient can try another card.
 *
 * Deliberately NOT using apiFetch — that helper attaches the Supabase
 * session; this page must work with zero auth state.
 */

type SessionState = {
  state: 'open' | 'resolved';
  first_name: string | null;
  product_label: string | null;
  amount_due_cents: number | null;
  currency: string | null;
  card_brand: string | null;
  card_last4: string | null;
  next_payment_attempt: string | null;
  product_image_url: string | null;
};

/**
 * Stripe's raw messages are written for developers, not patients. "A
 * processing error occurred." tells someone nothing about what to do
 * next, and reads like the site is broken rather than the card being
 * refused. Map the codes we can actually act on; anything unrecognised
 * falls through to Stripe's own text, which is at least accurate.
 */
function friendlyCardError(
  code: string | undefined,
  declineCode: string | undefined,
  fallback: string | undefined
): string {
  if (declineCode === 'insufficient_funds') {
    return 'That card was declined for insufficient funds. Try a different card.';
  }
  if (declineCode === 'expired_card' || code === 'expired_card') {
    return 'That card has expired. Try a different card.';
  }
  if (code === 'incorrect_cvc' || declineCode === 'incorrect_cvc') {
    return 'The security code didn’t match. Check the CVC and try again.';
  }
  if (code === 'incorrect_number' || code === 'invalid_number') {
    return 'That card number doesn’t look right. Double-check it and try again.';
  }
  if (code === 'processing_error') {
    return 'Your bank couldn’t process that card just now. Try again, or use a different card.';
  }
  if (code === 'card_declined') {
    return 'Your bank declined that card. Try a different card, or call them to approve the charge.';
  }
  return fallback || 'We couldn’t save that card. Try again, or use a different card.';
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'invalid' } // bad/expired token — generic, no oracle
  // Our side is broken, not their link (Stripe down, backend 5xx). Kept
  // separate because telling someone with a PERFECTLY VALID link that it
  // "expired" sends them off to sign in and re-hunt for a payment page
  // that was one retry away from working.
  | { kind: 'unavailable' }
  | { kind: 'resolved' } // nothing owed (already paid / sub ended)
  | { kind: 'open'; session: SessionState }
  | { kind: 'success' };

/** Link credential: `c` is the short slug (v2, SMS-friendly), `t` the
 *  original signed JWT (v1). Whichever the URL carries is forwarded. */
type LinkCred = { param: 'c' | 't'; value: string };

async function dunningFetch<T>(
  path: string,
  cred: LinkCred,
  init: RequestInit = {}
): Promise<T> {
  const sep = path.includes('?') ? '&' : '?';
  const res = await fetch(
    // apiEndpoint only builds the URL (absolute or same-origin relative);
    // no session is attached.
    apiEndpoint(`${path}${sep}${cred.param}=${encodeURIComponent(cred.value)}`),
    {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: { code?: string } })?.detail;
    const code = typeof detail === 'object' && detail ? detail.code : undefined;
    throw Object.assign(new Error(code ?? `http_${res.status}`), {
      status: res.status,
      code,
    });
  }
  return (await res.json()) as T;
}

function formatAmount(cents: number | null): string | null {
  if (!cents || cents <= 0) return null;
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

export function UpdatePaymentScreen() {
  const cred: LinkCred | null = (() => {
    if (typeof window === 'undefined') return null;
    const qs = new URLSearchParams(window.location.search);
    const c = qs.get('c');
    if (c) return { param: 'c', value: c };
    const t = qs.get('t');
    if (t) return { param: 't', value: t };
    return null;
  })();
  const [phase, setPhase] = useState<Phase>(
    cred ? { kind: 'loading' } : { kind: 'invalid' }
  );
  const startedRef = useRef(false);

  useEffect(() => {
    if (!cred || startedRef.current) return;
    startedRef.current = true;
    dunningFetch<SessionState>('/billing/dunning-session', cred)
      .then((session) => {
        // This page had NO analytics at all, so we couldn't tell a
        // working link from a broken one, or see how many patients open
        // it and never pay. Landing state is the top of that funnel.
        track('dunning_page_opened', {
          state: session.state,
          link_type: cred.param === 'c' ? 'slug' : 'jwt',
        });
        setPhase(
          session.state === 'open'
            ? { kind: 'open', session }
            : { kind: 'resolved' }
        );
      })
      .catch((err: { status?: number; code?: string }) => {
        // 5xx (or an explicit stripe_unavailable) means WE failed, not the
        // link. Only 4xx actually implies a bad/expired credential.
        const transient =
          (err?.status ?? 0) >= 500 || err?.code === 'stripe_unavailable';
        track('dunning_page_opened', {
          state: transient ? 'unavailable' : 'invalid',
          link_type: cred.param === 'c' ? 'slug' : 'jwt',
          status: err?.status ?? null,
          code: err?.code ?? null,
        });
        setPhase({ kind: transient ? 'unavailable' : 'invalid' });
      });
  }, [cred]);

  return (
    <Shell>
      {phase.kind === 'loading' && (
        <div className="flex items-center gap-10 type-body-md text-[#7C7C7C]">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      )}
      {phase.kind === 'invalid' && (
        <Message
          headline="This link has expired"
          body="For your security, payment links only work for a limited time. Sign in and we’ll take you straight to your payment methods."
          ctaHref="/login"
          ctaLabel="Sign in to update your card"
          ctaState={{
            from: { pathname: '/app/account', search: '?focus=payment' },
          }}
        />
      )}
      {phase.kind === 'unavailable' && (
        <div className="flex flex-col gap-25">
          <h1 className="type-headline-lg text-black">
            We couldn&apos;t load your payment details
          </h1>
          <p className="font-body text-15 text-[#7C7C7C]">
            Your link is fine — this one&apos;s on us. Give it a moment and try
            again. If it keeps happening, reply to the text or email we sent
            you and we&apos;ll sort it out.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center h-45 px-20 bg-black text-white type-body-sm hover:opacity-90 self-start"
          >
            Try again
          </button>
        </div>
      )}
      {phase.kind === 'resolved' && (
        <Message
          headline="You’re all set"
          body="There’s nothing due on this link — your payment has already been taken care of. You can sign in any time to manage your subscription."
          ctaHref="/login"
          ctaLabel="Go to your portal"
        />
      )}
      {phase.kind === 'success' && (
        <Message
          headline="Payment successful"
          body="Your new card is saved as your default and your renewal payment went through. Your treatment continues as scheduled — nothing else to do."
          ctaHref="/login"
          ctaLabel="Go to your portal"
        />
      )}
      {phase.kind === 'open' && (
        <OpenSession
          cred={cred!}
          session={phase.session}
          onPaid={() => setPhase({ kind: 'success' })}
        />
      )}
    </Shell>
  );
}

/* ---------- open session: summary + card form ---------- */

function OpenSession({
  cred,
  session,
  onPaid,
}: {
  cred: LinkCred;
  session: SessionState;
  onPaid: () => void;
}) {
  const amount = formatAmount(session.amount_due_cents);
  const what = session.product_label || 'your System Labs subscription';
  const greeting = session.first_name ? `Hi ${session.first_name} —` : '';

  return (
    <div className="flex flex-col gap-25">
      <h1 className="type-headline-lg text-black">Update your payment method</h1>
      {session.product_image_url && (
        // Reached from an SMS by someone who may not remember what they
        // subscribed to — showing the actual product is the difference
        // between "this is my treatment" and "this looks like phishing".
        <div className="flex items-center gap-15">
          <img
            src={session.product_image_url}
            alt={session.product_label ?? 'Your treatment'}
            className="w-70 h-70 object-cover bg-[var(--surface-sunken)] shrink-0"
            loading="lazy"
          />
          <div className="flex flex-col gap-2 min-w-0">
            <span className="font-mono text-14 uppercase text-black leading-110">
              {session.product_label}
            </span>
            {amount && (
              <span className="font-body text-14 text-[#7C7C7C]">
                {amount} due
              </span>
            )}
          </div>
        </div>
      )}
      <p className="font-body text-15 text-[#7C7C7C]">
        {greeting} we couldn’t process your renewal payment
        {amount ? ` of ${amount}` : ''} for {what}
        {session.card_brand && session.card_last4
          ? ` using your ${session.card_brand.toUpperCase()} card ending in ${session.card_last4}`
          : ''}
        . Add a new card below — we’ll make it your default and retry the
        payment right away.
      </p>

      <div
        role="alert"
        className="border-l-4 px-15 py-12"
        style={{ background: '#FBF1DE', borderColor: '#C47A00' }}
      >
        <p className="font-body text-14 font-semibold" style={{ color: '#C47A00' }}>
          Your next shipment is on hold — it will be delayed until your
          payment method is updated, and your subscription will be cancelled
          if payment can’t be collected.
        </p>
      </div>

      <div className="border border-black/10 bg-[#F7F7F7] px-15 py-12">
        <div className="font-mono text-12 uppercase text-[#7C7C7C]">
          Amount due
        </div>
        <div className="type-headline-sm text-black mt-2">
          {amount ?? '—'}
        </div>
        {session.product_label && (
          <div className="font-body text-13 text-[#7C7C7C] mt-2">
            {session.product_label}
          </div>
        )}
      </div>

      <DunningCardForm cred={cred} onPaid={onPaid} />

      <p className="font-body text-12 text-[#A0A0A0]">
        Payments are processed securely by Stripe. Your new card becomes the
        default for future renewals. Questions? Email support@systemlabs.com.
      </p>
    </div>
  );
}

function DunningCardForm({
  cred,
  onPaid,
}: {
  cred: LinkCred;
  onPaid: () => void;
}) {
  const [intent, setIntent] = useState<{
    client_secret: string;
    publishable_key: string;
  } | null>(null);
  const [intentError, setIntentError] = useState(false);
  // Error is held HERE, above <Elements>, so it survives the remount that
  // a fresh SetupIntent forces.
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  const mintIntent = useCallback(() => {
    setIntent(null);
    return dunningFetch<{ client_secret: string; publishable_key: string }>(
      '/billing/dunning-session/setup-intent',
      cred,
      { method: 'POST' }
    )
      .then(setIntent)
      .catch(() => setIntentError(true));
  }, [cred]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void mintIntent();
  }, [mintIntent]);

  /**
   * A SetupIntent is single-use. Cards that fail only when CHARGED
   * (insufficient funds, most declines) still SUCCEED at setup — so the
   * intent is consumed, the invoice charge then fails, and every later
   * attempt on this page confirms an already-succeeded intent. Stripe
   * reports that as a bare "A processing error occurred", so a patient
   * whose first card is declined gets an unfixable error on every card
   * after it — including good ones — until they think to reload.
   *
   * So: whenever an attempt consumed the intent, mint a new one and
   * remount Elements (keyed on client_secret, since Stripe requires a
   * fresh Elements instance per secret). The message is preserved and
   * tells them to re-enter the card, because the form is now blank.
   */
  const handleConsumed = useCallback(
    (message: string) => {
      setError(message);
      void mintIntent();
    },
    [mintIntent]
  );

  if (intentError) {
    return (
      <p className="type-body-sm text-status-danger">
        Couldn’t start the card form. Refresh the page to try again.
      </p>
    );
  }
  if (!intent) {
    return (
      <div className="flex flex-col gap-10">
        {error && (
          <div role="alert" className="type-body-sm text-status-danger">
            {error}
          </div>
        )}
        <div className="flex items-center gap-10 type-body-sm text-[#7C7C7C]">
          <Loader2 size={14} className="animate-spin" /> Loading secure card form…
        </div>
      </div>
    );
  }
  return (
    <Elements
      key={intent.client_secret}
      stripe={getStripe(intent.publishable_key)}
      options={{ clientSecret: intent.client_secret }}
    >
      <DunningCardInner
        cred={cred}
        onPaid={onPaid}
        error={error}
        setError={setError}
        onIntentConsumed={handleConsumed}
      />
    </Elements>
  );
}

function DunningCardInner({
  cred,
  onPaid,
  error,
  setError,
  onIntentConsumed,
}: {
  cred: LinkCred;
  onPaid: () => void;
  error: string | null;
  setError: (msg: string | null) => void;
  onIntentConsumed: (message: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  // `useStripe()` resolves as soon as Stripe.js loads, but the
  // PaymentElement mounts separately in its own iframe and only then
  // selects a payment method. Gating the button on `stripe` alone left a
  // window — wide on slow mobile connections, which is most of this
  // page's traffic since it's reached from a dunning SMS — where a tap
  // called confirmSetup with nothing selected, and Stripe answered
  // "Please select a payment method to pay with." Patients read that as
  // the page being broken. Wait for the element's own ready event.
  const [elementReady, setElementReady] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || submitting || !elementReady) return;
    setSubmitting(true);
    setError(null);
    track('dunning_payment_submitted');

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });
    if (confirmError) {
      track('dunning_payment_failed', {
        stage: 'confirm_setup',
        code: confirmError.code ?? null,
        decline_code: confirmError.decline_code ?? null,
      });
      setSubmitting(false);
      // The intent was already used up by an earlier attempt (this is the
      // bare "processing error" patients were seeing). Swap in a fresh one
      // instead of leaving them stuck on a dead form.
      if (confirmError.code === 'setup_intent_unexpected_state') {
        onIntentConsumed(
          'That attempt didn’t go through. Re-enter your card details and try again.'
        );
        return;
      }
      setError(
        friendlyCardError(
          confirmError.code,
          confirmError.decline_code,
          confirmError.message
        )
      );
      return;
    }

    const pm = setupIntent?.payment_method;
    const pmId = typeof pm === 'string' ? pm : (pm?.id ?? null);
    if (!pmId) {
      setError('Could not save card. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      const result = await dunningFetch<{ paid: boolean; error: string | null }>(
        '/billing/dunning-session/complete',
        cred,
        {
          method: 'POST',
          body: JSON.stringify({ payment_method_id: pmId }),
        }
      );
      if (result.paid) {
        track('dunning_payment_succeeded');
        onPaid();
        return;
      }
      // The card SAVED fine (so the SetupIntent is spent) but the charge
      // declined — the common case, since insufficient-funds and most
      // declines only surface when money actually moves. Retrying on this
      // spent intent is what produced the mystery "processing error", so
      // mint a fresh one and ask for the card again.
      track('dunning_payment_failed', { stage: 'charge_declined' });
      setSubmitting(false);
      onIntentConsumed(
        `${
          result.error ||
          'Your card was saved but the payment didn’t go through.'
        } Try a different card — re-enter the details below.`
      );
      return;
    } catch {
      track('dunning_payment_failed', { stage: 'complete_request' });
      setError('Something went wrong completing the payment. Please try again.');
    }
    setSubmitting(false);
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-12">
      <PaymentElement
        options={{
          layout: 'tabs',
          // Link is OFF here on purpose. Its inline enrollment ("save my
          // info for faster checkout") expands email + phone fields, and
          // completing them made confirmSetup fail with "A processing
          // error occurred" — reproducible: fill them in and it errors,
          // reload without touching them and the same card succeeds. Our
          // SetupIntent is payment_method_types: ['card'], so a Link
          // enrollment has nothing valid to resolve to.
          //
          // Even if that were fixed, this is the wrong page for an
          // upsell: the patient is here to clear a failed charge before
          // their subscription cancels. Every extra field is a chance to
          // abandon. Apple/Google Pay stay on — they're one tap and
          // carry no enrollment step.
          wallets: { applePay: 'auto', googlePay: 'auto', link: 'never' },
        }}
        onReady={() => setElementReady(true)}
      />
      {error && (
        <div role="alert" className="type-body-sm text-status-danger">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || !elementReady || submitting}
        className="flex items-center justify-center gap-8 h-45 px-15 bg-black text-white type-body-sm hover:opacity-90 disabled:opacity-60"
      >
        {(submitting || !elementReady) && (
          <Loader2 size={14} className="animate-spin" />
        )}
        {submitting
          ? 'Processing…'
          : elementReady
            ? 'Update card & pay now'
            : 'Loading card form…'}
      </button>
    </form>
  );
}

/* ---------- chrome (mirrors LoginScreen's standalone shell) ---------- */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-black">
      <header className="flex items-center justify-between border-b border-black/10 bg-[#ECECEC]">
        <div className="w-[5rem] h-[5rem] flex items-center justify-center bg-slate">
          <div className="w-full max-w-[3rem] text-black">
            <Icon name="Mark Line" viewBox="0 0 32 30" />
          </div>
        </div>
        <div className="flex items-center gap-15 px-15">
          <span className="font-mono text-12 uppercase text-black">
            Secure payment update
          </span>
          {/* Escape hatch to the real portal. Without it this page is a
              cul-de-sac: a patient who'd rather check their account,
              message support, or just confirm we're who we say we are
              has nowhere to go but the browser back button. */}
          <Link
            to="/app"
            className="font-mono text-12 uppercase text-black underline underline-offset-4 hover:opacity-70"
          >
            Open portal
          </Link>
        </div>
      </header>
      <main className="flex-1 flex flex-col px-15 md:px-30">
        <div className="w-full max-w-[44rem] mx-auto flex-1 flex flex-col justify-center pt-60 pb-60 md:pt-100 md:pb-100">
          {children}
        </div>
      </main>
    </div>
  );
}

function Message({
  headline,
  body,
  ctaHref,
  ctaLabel,
  ctaState,
}: {
  headline: string;
  body: string;
  ctaHref: string;
  ctaLabel: string;
  /** Router state forwarded to /login. LoginScreen reads `from` and sends
   *  the patient there after sign-in, so an expired link still ends at the
   *  payment form instead of dumping them on a generic home screen. */
  ctaState?: { from: { pathname: string; search?: string } };
}) {
  return (
    <div className="flex flex-col gap-25">
      <h1 className="type-headline-lg text-black">{headline}</h1>
      <p className="font-body text-15 text-[#7C7C7C]">{body}</p>
      <Link
        to={ctaHref}
        state={ctaState}
        className="inline-flex items-center justify-center h-45 px-20 bg-black text-white type-body-sm hover:opacity-90 self-start"
      >
        {ctaLabel}
      </Link>
    </div>
  );
}
